import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { TID } from "@/constants/testIds";
import {
  ShoppingCart,
  Banknote,
  Receipt,
  BarChart3,
  Link2,
  LogOut,
  CircleAlert,
  ShieldCheck,
  Settings2,
} from "lucide-react";

const navItems = [
  { to: "/pos",      label: "Mostrador",  icon: ShoppingCart, tid: TID.navPos },
  { to: "/touch",    label: "Modo Táctil", icon: ShoppingCart, tid: TID.navTouch },
  { to: "/cash",     label: "Caja",       icon: Banknote,     tid: TID.navCash },
  { to: "/tickets",  label: "Tickets",    icon: Receipt,      tid: TID.navTickets },
  { to: "/reports",  label: "Informes",   icon: BarChart3,    tid: TID.navReports },
  { to: "/settings", label: "Ajustes",    icon: Settings2,    tid: TID.navSettings },
];

export default function Layout({ children }) {
  const { user, session, sage, chain, logout } = useApp();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="pos-shell flex flex-col bg-slate-50">
      {/* Top bar */}
      <header className="h-14 shrink-0 border-b border-slate-200 bg-white flex items-center px-4 gap-4">
        <div className="flex items-center gap-2 mr-2">
          <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm">T</div>
          <div className="font-display font-semibold text-slate-900 hidden sm:block">TPV Retail</div>
        </div>

        <nav className="flex items-center gap-1">
          {navItems.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `h-9 px-3 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon size={16} /> <span className="hidden sm:inline">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {/* Sage status */}
          <div
            data-testid={TID.headerSage}
            className="flex items-center gap-2 px-3 h-9 rounded-md border border-slate-200 bg-white text-xs"
            title={sage.label}
          >
            <span className={`status-dot ${sage.online ? "online" : "offline"}`} />
            <span className="font-medium text-slate-700">
              Sage 200c · <span className={sage.online ? "text-emerald-700" : "text-amber-700"}>{sage.online ? "Online" : "Offline"}</span>
            </span>
            {sage.pending_queue > 0 && (
              <span className="ml-1 px-1.5 rounded bg-amber-100 text-amber-800 font-mono">{sage.pending_queue}</span>
            )}
          </div>

          {/* Verifactu chain */}
          <div
            data-testid={TID.headerChain}
            className="hidden md:flex items-center gap-2 px-3 h-9 rounded-md border border-slate-200 bg-white text-xs"
            title={chain.valid ? "Cadena Verifactu íntegra" : "Cadena rota"}
          >
            {chain.valid ? <ShieldCheck size={14} className="text-emerald-600" /> : <CircleAlert size={14} className="text-red-600" />}
            <Link2 size={12} className="text-slate-400" />
            <span className="font-mono text-slate-700">{chain.total_records}</span>
            <span className="text-slate-500">reg.</span>
          </div>

          {/* Session cash status */}
          {session ? (
            <div className="hidden lg:flex items-center gap-2 px-3 h-9 rounded-md bg-emerald-50 border border-emerald-200 text-xs">
              <span className="status-dot online" />
              <span className="font-medium text-emerald-800">Caja abierta</span>
              <span className="text-emerald-700 font-mono">€{Number(session.opening_amount).toFixed(2)}</span>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-2 px-3 h-9 rounded-md bg-amber-50 border border-amber-200 text-xs">
              <span className="status-dot offline" />
              <span className="font-medium text-amber-800">Caja cerrada</span>
            </div>
          )}

          {/* User */}
          {user && (
            <div data-testid={TID.headerUser} className="hidden sm:flex items-center gap-2 text-sm">
              <div className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-semibold">
                {user.name?.[0]?.toUpperCase()}
              </div>
              <div className="leading-tight">
                <div className="font-medium text-slate-800">{user.name}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">{user.role}</div>
              </div>
            </div>
          )}

          <button
            data-testid={TID.headerLogout}
            onClick={onLogout}
            className="h-9 px-3 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors flex items-center gap-1.5 text-sm"
            title="Cerrar sesión"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-hidden">{children}</main>
    </div>
  );
}
